import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import * as api from '../services/api';
import {
    ButtonPrimary,
    ButtonSecondary,
    LessonCard,
    Badge,
    Spinner,
    Alert,
    Icon
} from '../components/ComponentLibrary';
import { Trophy, Clock, Target, RotateCcw, Lock } from 'lucide-react';
import logoImg from '../img/Untitled design (19).png';
import './FinalQuizzesList.css';

export default function FinalQuizzesList() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchQuizzes = async () => {
            try {
                setLoading(true);
                const data = await api.getStudentDashboard();
                setQuizzes(data.availableQuizzes || []);
            } catch (err) {
                console.error('Fetch quizzes error:', err);
                setError('عفواً، فشل تحميل قائمة الامتحانات النهائية.');
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchQuizzes();
        }
    }, [user]);

    if (loading) {
        return (
            <div className="final-loading">
                <Spinner size="lg" />
                <p>جاري تحضير تحدياتك النهائية...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="final-error">
                <Alert variant="error">{error}</Alert>
            </div>
        );
    }

    return (
        <div className="final-quizzes-page">
            <header className="final-header">
                <div className="header-icon-box">
                    <img src={logoImg} alt="Code Wave Logo" className="final-header-logo" />
                </div>
                <h1 className="final-title">الامتحانات النهائية</h1>
                <p className="final-subtitle">تحدَّ نفسك واثبت مهارتك في عالم البرمجة!</p>
            </header>

            <div className="final-grid">
                {quizzes && quizzes.length > 0 ? (
                    quizzes.map((quiz, index) => {
                        const isUnlocked = quiz.isTrackMatch;
                        const isCompleted = quiz.passed;

                        return (
                            <div key={quiz.quizId} className={`final-item-wrapper ${!isUnlocked ? 'is-locked' : ''}`}>
                                <div className="final-card-main">
                                    <div className="card-top">
                                        <div className="track-badge">
                                            {quiz.track === 'scratch' ? <><Icon name="play" size={14} /> Scratch</> : <><Icon name="target" size={14} /> Web Dev</>}
                                        </div>
                                        {isCompleted ? (
                                            <Badge variant="success">تم الإنجاز <Icon name="check" size={12} /></Badge>
                                        ) : isUnlocked ? (
                                            <Badge variant="ocean">متاح للتحدي</Badge>
                                        ) : (
                                            <Badge variant="gray">مغلق</Badge>
                                        )}
                                    </div>

                                    <h3 className="quiz-name">{quiz.title}</h3>
                                    <p className="quiz-desc">{quiz.description}</p>

                                    {isUnlocked ? (
                                        <div className="quiz-meta-info">
                                            <div className="meta-item">
                                                <Clock size={16} />
                                                <span>{quiz.timeLimit} دقيقة</span>
                                            </div>
                                            <div className="meta-item">
                                                <Target size={16} />
                                                <span>درجة النجاح: {quiz.passingScore}%</span>
                                            </div>
                                            <div className="meta-item">
                                                <RotateCcw size={16} />
                                                <span>المحاولات: {quiz.attemptCount}/{quiz.maxAttempts}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="locked-info">
                                            <Lock size={32} />
                                            <p>هذا الامتحان مخصص لمسار آخر</p>
                                        </div>
                                    )}

                                    <div className="card-actions">
                                        {isCompleted ? (
                                            <ButtonSecondary
                                                className="w-full"
                                                onClick={() => navigate(`/take-quiz/${quiz.quizId}`)}
                                            >
                                                إعادة التحدي <Icon name="streak" size={18} />
                                            </ButtonSecondary>
                                        ) : isUnlocked && quiz.canAttempt ? (
                                            <ButtonPrimary
                                                className="w-full"
                                                onClick={() => navigate(`/take-quiz/${quiz.quizId}`)}
                                            >
                                                ابدأ الامتحان الآن <Icon name="rocket" size={18} />
                                            </ButtonPrimary>
                                        ) : (
                                            <ButtonPrimary disabled className="w-full">
                                                غير متاح
                                            </ButtonPrimary>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="empty-final">
                        <div className="empty-icon"><Icon name="mail" size={48} /></div>
                        <h3>لا توجد امتحانات متاحة حالياً</h3>
                        <p>سيقوم المعلم بإضافة التحديات قريباً، استعد!</p>
                    </div>
                )}
            </div>
        </div>
    );
}
